import { CloseOutlined, InboxOutlined } from "@ant-design/icons"
import { Drawer, Form, Input, Select, Upload } from "antd"
import { AppDownloadTemplateButton, Button, H6 } from "components"
import { useEffect, useState } from "react"
import checkBlue from "../../../../assets/images/check_blue.svg"
import { newArchAxiosInstance } from "utils/request"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { onQueryError } from "utils/utils"
import { useAppNotificationStore } from "stores"
import { useGet } from "utils/useFetch"
import { IDptListing } from "../components/objectives/types"
import { okrLabels } from "./okrLabels"

type props = {
  open: boolean
  handleClose: () => void
  selectedCycle: number | null
  handleOnSuccess: () => void
  role: "admin" | "employee" | "manager"
  isPersonCentric?: boolean
} & (
  | { type: "company" | "department"; deptId?: never }
  | { type: "individual"; deptId: number | null }
)
const { Dragger } = Upload

const BulkUploadObj = ({
  open,
  handleClose,
  selectedCycle,
  handleOnSuccess,
  type = "company",
  deptId,
  role,
  isPersonCentric = false,
}: props) => {
  const { toast } = useAppNotificationStore()
  const [file, setFile] = useState<File | null>(null)
  const [email, setEmail] = useState<string>("")
  const [selectedDptId, setSelectedDptId] = useState<number | null>(
    deptId || null
  )

  useEffect(() => {
    if (type === "individual" && deptId) {
      setSelectedDptId(deptId)
    }
  }, [type, deptId])

  const queryClient = useQueryClient()

  // Both selectedDptId and selectedCycle must be present when type === "individual"
  const canSubmit =
    !!file && !!selectedCycle && (type !== "individual" || !!selectedDptId)

  const canDownload =
    !!selectedCycle && (type !== "individual" || !!selectedDptId)

  const canSelectDpt =
    (role === "admin" && type === "individual") ||
    (role === "manager" && type === "individual")

  const label = okrLabels[type]

  const templateData = [
    `${label.singular} title and description fields`,
    "Period and quarter selection",
  ]

  const { isLoading: isLoadingDpt, data: dptListing } = useGet<IDptListing>({
    url:
      role === "manager"
        ? `/api/v2/manager/okr/departments`
        : `/api/v2/${role}/performance/departments`,
    instanceKey: "newArchAxiosInstance",
  })

  const { isLoading: isSubmitting, mutate: submit } = useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData()
      formData.append("file", file)

      // /api/v{version}/admin/okr/companyobjectivetemplate
      // /api/v{version}/admin/okr/companyobjectivetemplate/{cycleDurationId}

      // /api/v{version}/admin/okr/departmentobjectivetemplate/{cycleDurationId}
      // /api/v{version}/admin/okr/departmentobjectivetemplate/{cycleDurationId}

      // /api/v{version}/admin/okr/employeekeyresulttemplate/{departmentId}/{cycleDurationId}
      ///api/v{version}/admin/okr/employeekeyresulttemplate/{departmentId}/{cycleDurationId}

      const emailParam = email
        ? `emailAddress=${encodeURIComponent(email)}`
        : ""

      // Person-centric key results upload under the okrframework path;
      // objective-led ones sit directly under okr.
      const krTemplateSegment = isPersonCentric
        ? "okr/okrframework/employeekeyresulttemplate"
        : "okr/employeekeyresulttemplate"

      const endpoint =
        type === "company"
          ? `/api/v2/admin/okr/companyobjectivetemplate/${selectedCycle}${
              emailParam ? `?${emailParam}` : ""
            }`
          : type === "department"
          ? `/api/v2/${
              role === "manager" ? "manager" : "admin"
            }/okr/departmentobjectivetemplate/${selectedCycle}${
              emailParam ? `?${emailParam}` : ""
            }`
          : role === "admin"
          ? `/api/v2/admin/${krTemplateSegment}/${selectedDptId}/${selectedCycle}${
              emailParam ? `?${emailParam}` : ""
            }`
          : `/api/v2/manager/${krTemplateSegment}/${selectedCycle}${
              emailParam ? `?${emailParam}` : ""
            }`

      const res = await newArchAxiosInstance.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      })

      return res.data
    },

    onSuccess: (data) => {
      if (data.success) {
        queryClient.invalidateQueries({
          queryKey: [`/api/v2/admin/okr/${type}objectives`],
        })
        data.message && toast.success(data.message)
        handleOnSuccess()
      }
    },

    onError: onQueryError,
  })

  return (
    <Drawer
      width={575}
      title={
        <div className="flex flex-col ">
          <span className="text-padeBlack font-circular text-lg">
            Bulk Upload {label.plural}
          </span>
          <span className="mt-1 font-normal text-sm text-padeSubtext font-avenir">
            Upload multiple {label.lower} at once using an Excel file
          </span>
        </div>
      }
      open={open}
      onClose={handleClose}
      closeIcon={false}
      className="bg-white relative"
      extra={
        <button onClick={handleClose}>
          <CloseOutlined className="h-6 w-6" />
        </button>
      }
      footer={
        <div>
          <Button
            color="primary"
            submitting={isSubmitting}
            disabled={!canSubmit}
            onClick={() => {
              if (canSubmit) submit(file!)
            }}
            className="w-full"
          >
            Upload
          </Button>
        </div>
      }
    >
      <div className=" font-circular">
        {canSelectDpt && (
          <div className="flex flex-col gap-1 mb-4">
            <span className="font-semibold font-circular text-padeBlack">
              Department
            </span>
            <Select
              loading={isLoadingDpt}
              placeholder="Select Department"
              options={dptListing?.data?.docs?.map((dpt) => ({
                label: dpt.nameOfDepartment,
                value: dpt.departmentId?.toString(),
              }))}
              filterOption={(
                input: string,
                option?: {
                  label: string
                  value: string
                }
              ) => {
                if (option) {
                  const label = option.label || ""

                  return label?.toLowerCase().indexOf(input?.toLowerCase()) >= 0
                } else {
                  return false
                }
              }}
              showSearch
              disabled={!!deptId}
              className="w-full"
              value={selectedDptId?.toString()}
              onChange={(value) => setSelectedDptId(Number(value))}
            />
          </div>
        )}

        <div className="flex flex-col gap-1 mb-6">
          <span className="font-semibold font-circular text-padeBlack">
            Email Address (optional)
          </span>
          <Input
            placeholder="Email address where the processed file will be sent."
            value={email}
            onChange={(e) => {
              setEmail(e.target.value)
            }}
            type="email"
          />
        </div>

        <div className=" border py-5 px-6 rounded mb-6">
          <h6 className="font-semibold font-circular text-padeBlack">
            Download Template
          </h6>
          <p className="mt-1 mb-4 text-padeSubtext text-sm font-avenir">
            Start by downloading our Excel template with the required columns
            and format.{" "}
          </p>
          <p className="font-semibold font-circular text-padeBlack mb-3">
            Template Includes
          </p>
          {templateData.map((item, index) => (
            <div
              key={index}
              className="flex items-center gap-2 mb-4"
            >
              <img
                src={checkBlue}
                alt=""
              />
              <p>{item}</p>
            </div>
          ))}
          {/* <Button className="mt-5 text-padeBlack"></Button> */}
          <AppDownloadTemplateButton
            url={
              type === "company"
                ? `/api/v2/admin/okr/companyobjectivetemplate?FileType=1`
                : type === "department"
                ? `/api/v2/${
                    role === "admin" ? "admin" : "manager"
                  }/okr/departmentobjectivetemplate/${selectedCycle}?FileType=1`
                : role === "admin"
                ? `/api/v2/admin/okr/employeekeyresulttemplate/${selectedDptId}/${selectedCycle}?FileType=1`
                : `/api/v2/manager/okr/employeekeyresulttemplate/${selectedCycle}?FileType=1`
            }
            fileName={`${type}obj`}
            title=" Download Template"
            type="xlsx"
            data={[]}
            method="GET"
            className="neutral-outline"
            instanceKey="newArchAxiosInstance"
            disabled={!canDownload}
          />
        </div>

        <div className="border py-5 px-6 rounded mb-6 space-y-4">
          <h6 className="font-semibold text-padeBlack font-circular">
            Upload File
          </h6>
          <p className="mt-1 mb-6 font-avenir text-sm text-padeSubtext">
            Start by downloading our Excel template with the required columns
            and format.
          </p>
          <Dragger
            accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
            multiple={false}
            maxCount={1}
            beforeUpload={(file) => {
              setFile(file)
              return false // ❗ prevents auto upload
            }}
            onRemove={() => {
              setFile(null)
            }}
            onDrop={(e) => {
              e.preventDefault()
              const droppedFile = e.dataTransfer.files[0]
              if (droppedFile) {
                setFile(droppedFile)
              }
            }}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>

            <H6 className="text-dark10 font-medium text-sm leading-[21px]">
              Drag & drop your required document here
            </H6>

            <Button
              color="primary"
              title="Choose file"
              className="mt-4 bg-primary600 text-white font-avenir"
              onClick={(e) => e.preventDefault()}
            >
              Choose file
            </Button>
          </Dragger>
        </div>
      </div>
    </Drawer>
  )
}

export default BulkUploadObj
